import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { WebStorageStateStore } from 'oidc-client-ts'
import { lazy, Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import {
  AuthProvider,
  type AuthProviderProps,
  useAuth
} from 'react-oidc-context'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider
} from 'react-router-dom'
import {
  DataLoadingPlaceholder,
  NotFound,
  QueryErrorBoundary
} from '@/components'
import { InactivityDialog } from '@/components/inactivity-dialog/InactivityDialog'
import { PwaInstallBanner } from '@/components/pwa-install-banner/PwaInstallBanner'
import { PrivateRoute } from '@/components/routes/PrivateRoute'
import { ScrollToTop } from '@/components/scroll-to-top/ScrollToTop'
import { Snackbar } from '@/components/snackbar/Snackbar'
import { AuthBridge } from '@/context/identity-provider/IdentityProvider'
import { PermissionsBridge } from '@/context/permissions-provider/PermissionsProvider'
import { useCustomEventCommunication } from '@/hooks/useCustomEventCommunication'
import { Layout } from '@/layout/Layout'
import { molsonTheme, ThemeProvider } from '@/theme'
import { PERMISSIONS, QuoteType } from '@/utils'
import { TrackingComponent } from '@/utils/TrackingComponent'

// ─── App Layout (replaces ShellRoot) ─────────────────────────────────────────
const AppLayout = () => {
  const { isAuthenticated } = useAuth()
  const { handleSignIn } = useCustomEventCommunication()
  return (
    <Layout>
      <InactivityDialog handleSignIn={handleSignIn} />
      {isAuthenticated ? <TrackingComponent /> : null}
      <Outlet />
      <ScrollToTop />
      <Snackbar />
      <PwaInstallBanner />
    </Layout>
  )
}

// ─── Pages ────────────────────────────────────────────────────────────────────
const LoginPage = lazy(() =>
  import('@/pages/login/Login').then(m => ({ default: m.LoginPage }))
)

// ─── Customers ────────────────────────────────────────────────────────────────
const CustomersRoot = lazy(() =>
  import('@/pages/customers/app/app').then(m => ({ default: m.CustomersRoot }))
)
const Customers = lazy(() =>
  import('@/pages/customers/pages/customers/Customers').then(m => ({
    default: m.Customers
  }))
)
const CustomerProfile = lazy(() =>
  import('@/pages/customers/pages/customer-profile/CustomerProfile').then(
    m => ({ default: m.CustomerProfile })
  )
)
const CustomerSpecView = lazy(() =>
  import(
    '@/pages/customers/pages/customer-spec-detail-view/CustomerSpecView'
  ).then(m => ({ default: m.CustomerSpecView }))
)
const NewCustomer = lazy(() =>
  import('@/pages/customers/pages/new-customer/NewCustomer').then(m => ({
    default: m.NewCustomer
  }))
)
const CustomersEquipmentBuilder = lazy(() =>
  import('@/pages/customers/pages/equipment-builder/EquipmentBuilder').then(
    m => ({ default: m.EquipmentBuilder })
  )
)
const CustomersAttachmentBuilder = lazy(() =>
  import('@/pages/customers/pages/attachment-builder/AttachmentBuilder').then(
    m => ({ default: m.AttachmentBuilder })
  )
)

// ─── Data Management ──────────────────────────────────────────────────────────
const DataManagementRoot = lazy(() =>
  import('@/pages/data-management/app/app').then(m => ({
    default: m.DataManagementRoot
  }))
)
const ManageBrandsOptions = lazy(() =>
  import(
    '@/pages/data-management/pages/manage-brands-options/ManageBrandsOptions'
  ).then(m => ({ default: m.BrandsTable }))
)
const ArrangeBrandsOptions = lazy(() =>
  import(
    '@/pages/data-management/pages/manage-brands-options/components/brands-table/ArrangeBrands'
  ).then(m => ({ default: m.ArrangeBrands }))
)
const BrandMachines = lazy(() =>
  import(
    '@/pages/data-management/pages/manage-brands-options/components/brand-machines/BrandMachines'
  ).then(m => ({ default: m.BrandMachines }))
)
const MachineDetails = lazy(() =>
  import(
    '@/pages/data-management/pages/manage-brands-options/components/machine-details/MachineDetails'
  ).then(m => ({ default: m.MachineDetails }))
)
const PricingIndexes = lazy(() =>
  import('@/pages/data-management/pages/pricing-indexes/PricingIndexes').then(
    m => ({ default: m.PricingIndexes })
  )
)
const Upload = lazy(() =>
  import('@/pages/data-management/pages/upload/Upload').then(m => ({
    default: m.Upload
  }))
)
const FileUpload = lazy(() =>
  import('@/pages/data-management/pages/file-upload/FileUpload').then(m => ({
    default: m.FileUpload
  }))
)
const Configure = lazy(() =>
  import('@/pages/data-management/pages/configure/Configure').then(m => ({
    default: m.Configure
  }))
)
const BaseUnit = lazy(() =>
  import(
    '@/pages/data-management/pages/configure/pages/base-unit/BaseUnit'
  ).then(m => ({ default: m.BaseUnit }))
)
const FactoryFit = lazy(() =>
  import(
    '@/pages/data-management/pages/configure/pages/factory-fit/FactoryFit'
  ).then(m => ({ default: m.FactoryFit }))
)
const DealerFit = lazy(() =>
  import(
    '@/pages/data-management/pages/configure/pages/dealer-fit/DealerFit'
  ).then(m => ({ default: m.DealerFit }))
)
const Rules = lazy(() =>
  import('@/pages/data-management/pages/rules/Rules').then(m => ({
    default: m.Rules
  }))
)
const ConfigureRules = lazy(() =>
  import('@/pages/data-management/pages/configure-rules/ConfigureRules').then(
    m => ({ default: m.ConfigureRules })
  )
)
const NewDataWizard = lazy(() =>
  import('@/pages/data-management/pages/new-data-wizard/NewDataWizard').then(
    m => ({ default: m.NewDataWizard })
  )
)

// ─── Quotation Tool ───────────────────────────────────────────────────────────
const QuotationRoot = lazy(() =>
  import('@/pages/quotation-tool/app/app').then(m => ({
    default: m.QuotationRoot
  }))
)
const QuotesPage = lazy(() =>
  import('@/pages/quotation-tool/pages/quotes/Quotes').then(m => ({
    default: m.Quotes
  }))
)
const QuotePage = lazy(() =>
  import('@/pages/quotation-tool/pages/quote/Quote').then(m => ({
    default: m.Quote
  }))
)
const QuickPriceTool = lazy(() =>
  import('@/pages/quotation-tool/pages/quick-price/QuickPriceTool').then(m => ({
    default: m.QuickPriceTool
  }))
)
const QuoteConfirmationPage = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/quote-confirmation/QuoteConfirmation'
  ).then(m => ({ default: m.QuoteConfirmation }))
)
const PreviewQuoteConfirmationPage = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/preview-quote-confirmation/PreviewQuoteConfirmation'
  ).then(m => ({ default: m.PreviewQuoteConfirmation }))
)
const QuoteSummary = lazy(() =>
  import('@/pages/quotation-tool/pages/quote-summary/QuoteSummary').then(m => ({
    default: m.QuoteSummary
  }))
)
const MobileViewRestricted = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/mobile-restricted-view/MobileViewRestricted'
  ).then(m => ({ default: m.MobileViewRestricted }))
)
const SpecDetailView = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/saved-specs/components/customer-spec-detail-view/CustomerSpecView'
  ).then(m => ({ default: m.CustomerSpecView }))
)
const QuotationEquipmentBuilder = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/equipment-builder/EquipmentBuilder'
  ).then(m => ({ default: m.EquipmentBuilder }))
)
const QuotationAttachmentBuilder = lazy(() =>
  import(
    '@/pages/quotation-tool/pages/attachment-builder/AttachmentBuilder'
  ).then(m => ({ default: m.AttachmentBuilder }))
)

// ─── Admin ────────────────────────────────────────────────────────────────────
const AdminRoot = lazy(() =>
  import('@/pages/admin/app/app').then(m => ({ default: m.AdminRoot }))
)
const UsersPage = lazy(() =>
  import('@/pages/admin/pages/users/Users').then(m => ({ default: m.Users }))
)
const UserInformationPage = lazy(() =>
  import('@/pages/admin/pages/user-information/UserInformation').then(m => ({
    default: m.UserInformation
  }))
)
const AccessRolePage = lazy(() =>
  import('@/pages/admin/pages/access-role/AccessRole').then(m => ({
    default: m.AccessRole
  }))
)
const ApprovalRulesPage = lazy(() =>
  import('@/pages/admin/pages/approval-rules/ApprovalRules').then(m => ({
    default: m.ApprovalRules
  }))
)
const RuleEnginePage = lazy(() =>
  import('@/pages/admin/pages/rule-engine/RuleEngine').then(m => ({
    default: m.RuleEngine
  }))
)
const SettingsPage = lazy(() =>
  import('@/pages/admin/pages/settings/Settings').then(m => ({
    default: m.Settings
  }))
)
const FileManagerPage = lazy(() =>
  import('@/pages/admin/pages/file-manager/FileManager').then(m => ({
    default: m.FileManager
  }))
)
const FeatureFlagsPage = lazy(() =>
  import('@/pages/admin/pages/feature-flags/FeatureFlags').then(m => ({
    default: m.FeatureFlags
  }))
)

// ─── Auth / QueryClient ───────────────────────────────────────────────────────
const REDIRECT_URI = globalThis.location.origin

export const oidcConfig: AuthProviderProps = {
  authority: import.meta.env.NX_AUTHORITY ?? '',
  client_id: import.meta.env.NX_CLIENT_ID ?? '',
  redirect_uri: REDIRECT_URI,
  post_logout_redirect_uri: REDIRECT_URI,
  loadUserInfo: true,
  userStore: new WebStorageStateStore({ store: sessionStorage }),
  scope: 'basic openid email profile pricecmd-api smartcmd-api',
  accessTokenExpiringNotificationTimeInSeconds: 30,
  extraQueryParams: { kc_idp_hint: 'azure-entra' }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: true,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: 3
    }
  }
})

// ─── Router ───────────────────────────────────────────────────────────────────
const loading = <DataLoadingPlaceholder text="Loading..." />

const wrap = (node: React.ReactNode) => (
  <QueryErrorBoundary>
    <Suspense fallback={loading}>{node}</Suspense>
  </QueryErrorBoundary>
)

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<AppLayout />}>
      <Route
        index={true}
        element={
          <Suspense fallback={loading}>
            <LoginPage />
          </Suspense>
        }
      />

      {/* ── Customers ──────────────────────────────────────────────────────── */}
      <Route
        path="customers"
        element={
          <PrivateRoute>
            <Suspense fallback={loading}>
              <CustomersRoot />
            </Suspense>
          </PrivateRoute>
        }
      >
        <Route index={true} element={wrap(<Customers />)} />
        <Route path="customer-profile/customer-id/:id">
          <Route index={true} element={wrap(<CustomerProfile />)} />
          <Route path="customer-spec/spec-id/:specId">
            <Route index={true} element={wrap(<CustomerSpecView />)} />
            <Route
              path="equipment-id/:equipmentId"
              element={wrap(<CustomersEquipmentBuilder />)}
            />
            <Route
              path="attachment-id/:attachmentId"
              element={wrap(<CustomersAttachmentBuilder />)}
            />
            <Route
              path="equipment-id/:equipmentId/attachment-id/:attachmentId"
              element={wrap(<CustomersAttachmentBuilder />)}
            />
          </Route>
        </Route>
        <Route path="create-new-customer" element={wrap(<NewCustomer />)} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Data Management ────────────────────────────────────────────────── */}
      <Route
        path="data-management"
        element={
          <PrivateRoute requiredPermissions={[PERMISSIONS.DATA_MANAGEMENT]}>
            <Suspense fallback={loading}>
              <DataManagementRoot />
            </Suspense>
          </PrivateRoute>
        }
      >
        <Route
          index={true}
          element={<Navigate to="manage-options" replace={true} />}
        />
        <Route path="manage-options">
          <Route index={true} element={wrap(<ManageBrandsOptions />)} />
          <Route path="reorder" element={wrap(<ArrangeBrandsOptions />)} />
          <Route path=":brand">
            <Route index={true} element={wrap(<BrandMachines />)} />
            <Route path=":machine" element={wrap(<MachineDetails />)} />
          </Route>
        </Route>
        <Route path="pricing-indexes" element={wrap(<PricingIndexes />)} />
        <Route path="upload">
          <Route index={true} element={wrap(<Upload />)} />
          <Route path="file-upload" element={wrap(<FileUpload />)} />
          <Route path="review" element={wrap(<NewDataWizard />)} />
        </Route>
        <Route path="configure">
          <Route index={true} element={wrap(<Configure />)} />
          <Route path="base-unit" element={wrap(<BaseUnit />)} />
          <Route path="factory-fit" element={wrap(<FactoryFit />)} />
          <Route path="dealer-fit" element={wrap(<DealerFit />)} />
        </Route>
        <Route path="rules">
          <Route index={true} element={wrap(<Rules />)} />
          <Route path="ruleId/:ruleId" element={wrap(<ConfigureRules />)} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Quotation Tool ─────────────────────────────────────────────────── */}
      <Route
        path="quotation-tool"
        element={
          <PrivateRoute>
            <Suspense fallback={loading}>
              <QuotationRoot />
            </Suspense>
          </PrivateRoute>
        }
      >
        <Route
          index={true}
          element={<Navigate to="my-quotes" replace={true} />}
        />
        <Route path="quick-price" element={wrap(<QuickPriceTool />)} />
        <Route
          path="restricted-view"
          element={wrap(<MobileViewRestricted />)}
        />
        <Route
          path="client-quote-confirmation"
          element={wrap(<QuoteConfirmationPage />)}
        />
        <Route
          path="preview-quote-confirmation"
          element={wrap(<PreviewQuoteConfirmationPage />)}
        />

        <Route path="my-quotes">
          <Route
            index={true}
            element={wrap(<QuotesPage quoteType={QuoteType.MY_QUOTES} />)}
          />
          <Route path="quote-id/:quoteId">
            <Route index={true} element={wrap(<QuotePage />)} />
            <Route
              path="saved-specs/spec-id/:specId"
              element={wrap(<SpecDetailView />)}
            />
            <Route path="quote-summary" element={wrap(<QuoteSummary />)} />
            <Route path="equipment-id/:equipmentId">
              <Route
                index={true}
                element={wrap(<QuotationEquipmentBuilder />)}
              />
              <Route
                path="attachment-id/:attachmentId"
                element={wrap(<QuotationAttachmentBuilder />)}
              />
            </Route>
            <Route
              path="attachment-id/:attachmentId"
              element={wrap(<QuotationAttachmentBuilder />)}
            />
          </Route>
        </Route>

        <Route path="all-quotes">
          <Route
            index={true}
            element={wrap(<QuotesPage quoteType={QuoteType.ALL_QUOTES} />)}
          />
          <Route path="quote-id/:quoteId">
            <Route index={true} element={wrap(<QuotePage />)} />
            <Route
              path="saved-specs/spec-id/:specId"
              element={wrap(<SpecDetailView />)}
            />
            <Route path="quote-summary" element={wrap(<QuoteSummary />)} />
            <Route path="equipment-id/:equipmentId">
              <Route
                index={true}
                element={wrap(<QuotationEquipmentBuilder />)}
              />
              <Route
                path="attachment-id/:attachmentId"
                element={wrap(<QuotationAttachmentBuilder />)}
              />
            </Route>
            <Route
              path="attachment-id/:attachmentId"
              element={wrap(<QuotationAttachmentBuilder />)}
            />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>

      {/* ── Admin ──────────────────────────────────────────────────────────── */}
      <Route
        path="admin"
        element={
          <PrivateRoute requiredPermissions={[PERMISSIONS.USERS]}>
            <Suspense fallback={loading}>
              <AdminRoot />
            </Suspense>
          </PrivateRoute>
        }
      >
        <Route index={true} element={<Navigate to="users" replace={true} />} />
        <Route path="users">
          <Route index={true} element={wrap(<UsersPage />)} />
          <Route
            path="userId/:userId"
            element={wrap(<UserInformationPage />)}
          />
          <Route
            path="accessRoleId/:accessRoleId"
            element={wrap(<AccessRolePage />)}
          />
        </Route>
        <Route path="approval-rules">
          <Route index={true} element={wrap(<ApprovalRulesPage />)} />
          <Route path="ruleId/:ruleId" element={wrap(<RuleEnginePage />)} />
        </Route>
        <Route path="settings" element={wrap(<SettingsPage />)} />
        <Route path="file-manager" element={wrap(<FileManagerPage />)} />
        <Route path="feature-flags" element={wrap(<FeatureFlagsPage />)} />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route
        path="*"
        element={
          <PrivateRoute>
            <NotFound />
          </PrivateRoute>
        }
      />
    </Route>
  )
)

// biome-ignore lint/style/noDefaultExport: <none>
export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={molsonTheme}>
        <AuthProvider {...oidcConfig}>
          <QueryClientProvider client={queryClient}>
            <AuthBridge />
            <PermissionsBridge />
            <RouterProvider router={router} />
          </QueryClientProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
}
